/*
 * Implementation inspired by phantom-ui (https://github.com/Aejkatappaja/phantom-ui),
 * MIT License, Copyright (c) 2026 Aejkatappaja. The leaf-element walk, measurement
 * override attributes, and radius fallback follow its `src/dom-measurement.ts`.
 */

import {
    clipRect,
    intersectClips,
    mergeLineRects,
    resolveBlockRadius,
    type SkeletonBlock,
    type SkeletonClip,
    type SkeletonRect,
    sortBlocksByReadingOrder,
    UNCLIPPED,
} from "./skeletonLayout";

/** Skip the element and its subtree. */
export const SKELETON_IGNORE_ATTRIBUTE = "data-skeleton-ignore";
/** Measure the element as one block without descending. */
export const SKELETON_NO_CHILDREN_ATTRIBUTE = "data-skeleton-no-children";
/** Override the measured width in px, e.g. for media without intrinsic size yet. Not clipped. */
export const SKELETON_WIDTH_ATTRIBUTE = "data-skeleton-width";
/** Override the measured height in px. Not clipped. */
export const SKELETON_HEIGHT_ATTRIBUTE = "data-skeleton-height";

const ALWAYS_LEAF_TAGS = new Set([
    "IMG",
    "SVG",
    "VIDEO",
    "CANVAS",
    "IFRAME",
    "INPUT",
    "TEXTAREA",
    "BUTTON",
    "HR",
]);

const VOID_TAGS = new Set(["BR", "WBR"]);

/** A clipping box this small hides its content, like the `sr-only` pattern. */
const MAX_HIDING_CLIP_PX = 1;

type MeasureOptions = {
    /** Radius in px for blocks whose element has none, like text. */
    fallbackRadius: number;
};

/**
 * Walks `content` like phantom-ui's `extractElementInfo`: every leaf element
 * (or `data-skeleton-no-children` element) with a visible size becomes a block,
 * positioned relative to `origin`. Elements with their own text are split into
 * one block per rendered line instead of one box. Blocks are trimmed to the
 * area left visible by `overflow` on their ancestors.
 */
export function measureSkeletonBlocks(
    content: HTMLElement,
    origin: DOMRect,
    { fallbackRadius }: MeasureOptions,
): SkeletonBlock[] {
    const blocks: SkeletonBlock[] = [];

    for (const child of Array.from(content.children)) {
        walk(child, UNCLIPPED);
    }

    return sortBlocksByReadingOrder(blocks);

    function walk(element: Element, clip: SkeletonClip) {
        if (element.hasAttribute(SKELETON_IGNORE_ATTRIBUTE)) {
            return;
        }

        const style = getComputedStyle(element);
        const box = toOriginRect(element.getBoundingClientRect(), origin);
        const isText = hasOwnText(element);
        const shouldCapture =
            element.hasAttribute(SKELETON_NO_CHILDREN_ATTRIBUTE) ||
            isLeafElement(element) ||
            isText;

        if (!shouldCapture) {
            // No size check here: zero-size wrappers like `display: contents` can
            // still have visible children.
            if (!hidesContent(style, box)) {
                const contentClip = getContentClip(style, box, clip);
                for (const child of Array.from(element.children)) {
                    walk(child, contentClip);
                }
            }
            return;
        }

        const overrideWidth =
            Number(element.getAttribute(SKELETON_WIDTH_ATTRIBUTE)) || 0;
        const overrideHeight =
            Number(element.getAttribute(SKELETON_HEIGHT_ATTRIBUTE)) || 0;

        if (overrideWidth > 0 || overrideHeight > 0) {
            addBlock(
                {
                    ...box,
                    width: overrideWidth || box.width,
                    height: overrideHeight || box.height,
                },
                style,
            );
            return;
        }

        // Text is also clipped by its own overflow (line clamps); boxes only by their ancestors'.
        const rects = isText
            ? measureVisibleTextLines(element, style, box, clip)
            : [clipRect(box, clip)];

        for (const rect of rects) {
            if (rect) {
                addBlock(rect, style);
            }
        }
    }

    function measureVisibleTextLines(
        element: Element,
        style: CSSStyleDeclaration,
        box: SkeletonRect,
        clip: SkeletonClip,
    ): (SkeletonRect | null)[] {
        if (hidesContent(style, box)) {
            return [];
        }

        const contentClip = getContentClip(style, box, clip);

        return measureTextLines(element, origin).map((line) =>
            clipRect(line, contentClip),
        );
    }

    function addBlock(rect: SkeletonRect, style: CSSStyleDeclaration) {
        const rounded = roundRect(rect);
        blocks.push({
            ...rounded,
            ...resolveBlockRadius(style.borderRadius, rounded, fallbackRadius),
        });
    }
}

/**
 * The elements `measureSkeletonBlocks` would turn into blocks, without
 * measuring: leaves, text elements, and `data-skeleton-no-children` elements.
 * Used on the real content to stagger its reveal like the outlines.
 */
export function collectSkeletonTargets(content: HTMLElement): Element[] {
    const targets: Element[] = [];

    for (const child of Array.from(content.children)) {
        walk(child);
    }

    return targets;

    function walk(element: Element) {
        if (element.hasAttribute(SKELETON_IGNORE_ATTRIBUTE)) {
            return;
        }

        if (
            element.hasAttribute(SKELETON_NO_CHILDREN_ATTRIBUTE) ||
            isLeafElement(element) ||
            hasOwnText(element)
        ) {
            targets.push(element);
            return;
        }

        for (const child of Array.from(element.children)) {
            walk(child);
        }
    }
}

function isLeafElement(element: Element): boolean {
    if (ALWAYS_LEAF_TAGS.has(element.tagName.toUpperCase())) {
        return true;
    }

    return Array.from(element.children).every((child) => VOID_TAGS.has(child.tagName));
}

function hasOwnText(element: Element): boolean {
    return Array.from(element.childNodes).some(
        (node) => node.nodeType === Node.TEXT_NODE && !!node.textContent?.trim(),
    );
}

function clipsX(style: CSSStyleDeclaration): boolean {
    return style.overflowX !== "visible";
}

function clipsY(style: CSSStyleDeclaration): boolean {
    return style.overflowY !== "visible";
}

function hidesContent(style: CSSStyleDeclaration, box: SkeletonRect): boolean {
    return (
        (clipsX(style) && box.width <= MAX_HIDING_CLIP_PX) ||
        (clipsY(style) && box.height <= MAX_HIDING_CLIP_PX)
    );
}

/** The area an element leaves visible for its content: its own box on each axis it clips. */
function getContentClip(
    style: CSSStyleDeclaration,
    box: SkeletonRect,
    clip: SkeletonClip,
): SkeletonClip {
    const isClippedX = clipsX(style);
    const isClippedY = clipsY(style);

    return intersectClips(clip, {
        left: isClippedX ? box.x : UNCLIPPED.left,
        top: isClippedY ? box.y : UNCLIPPED.top,
        right: isClippedX ? box.x + box.width : UNCLIPPED.right,
        bottom: isClippedY ? box.y + box.height : UNCLIPPED.bottom,
    });
}

function measureTextLines(element: Element, origin: DOMRect): SkeletonRect[] {
    const range = document.createRange();
    range.selectNodeContents(element);

    return mergeLineRects(
        Array.from(range.getClientRects()).map((rect) => toOriginRect(rect, origin)),
    );
}

function toOriginRect(rect: DOMRect, origin: DOMRect): SkeletonRect {
    return {
        x: rect.left - origin.left,
        y: rect.top - origin.top,
        width: rect.width,
        height: rect.height,
    };
}

function roundRect(rect: SkeletonRect): SkeletonRect {
    return {
        x: round(rect.x),
        y: round(rect.y),
        width: round(rect.width),
        height: round(rect.height),
    };
}

function round(value: number): number {
    return Math.round(value * 10) / 10;
}
